import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import useDialog from 'hooks/useDialog';

import { sendEvent } from 'lib/gtag';
import { isEmpty } from 'lib/object';
import { getUtmsFromSession } from 'lib/sessionStorage';
import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import { postSignIn, postSignUpUser } from 'services/user';

import EmailStep from './EmailStep';

import LOGIN from 'constants/login';

const SignInStep = dynamic(() => import('./SignInStep'), { ssr: false });

const SignUpStep = dynamic(() => import('./SignUpStep'), { ssr: false });

const SignUpSuccessDialog = dynamic(() => import('./SignUpSuccessDialog'), {
  ssr: false,
});

const GENERAL_ERROR_MESSAGES = {
  'account is disabled': null,
  401: LOGIN.AUTHENTICATION_FAILED,
  403: LOGIN.AUTHENTICATION_UNVERIFIED,
};

const NAME_ERROR_MESSAGES = {
  [LOGIN.ERRORS.NAME_REQUIRED]: LOGIN.FIELD_REQUIRED,
  'name contains invalid characters': LOGIN.NAME_NOT_VALID,
};

const PASSWORD_ERROR_MESSAGES = {
  [LOGIN.ERRORS.PASSWORD_REQUIRED]: LOGIN.FIELD_REQUIRED,
  [LOGIN.ERRORS.PASSWORD_TOO_SHORT]: LOGIN.PASSWORD_TOO_SHORT,
};

const EMAIL_ERROR_MESSAGES = {
  [LOGIN.ERRORS.EMAIL_REQUIRED]: LOGIN.FIELD_REQUIRED,
  [LOGIN.ERRORS.EMAIL_INVALID]: LOGIN.ERRORS.EMAIL_INVALID,
  [LOGIN.ERRORS.EMAIL_EXISTS]: LOGIN.ERRORS.EMAIL_EXISTS,
  'email is not verified': LOGIN.EMAIL_NOT_VERIFIED,
};

const STEPS = {
  initial: EmailStep,
  signin: SignInStep,
  signup: SignUpStep,
};

const Form = ({
  className,
  gaEventCategory,
  greetingsMessage,
  onClose,
  onSuccess = noop,
}) => {
  const signUpStepRef = useRef(null);
  const { login } = useUser();
  const { showDialog } = useDialog();
  const [currStep, setCurrStep] = useState('initial');
  const form = useForm({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      passwordConfirmation: '',
    },
  });

  const goToInitialStep = () => {
    setCurrStep('initial');
  };

  const goToSignInStep = () => {
    setCurrStep('signin');
  };

  const goToSignUpStep = () => {
    setCurrStep('signup');
  };

  const { mutate: mutateSignIn, reset: resetSignIn } = useMutation({
    mutationFn: postSignIn,
    onSuccess: async ({ token }) => {
      onSuccess();
      await login({ token });
      sendEvent({ action: 'login_success', category: gaEventCategory });
      resetSignIn();
    },
    onError: ({ message }) => {
      const emailError = EMAIL_ERROR_MESSAGES[message];
      const generalError = GENERAL_ERROR_MESSAGES[message];

      if (emailError || generalError) {
        form.setError('email', {
          type: 'request',
          message: emailError || generalError,
        });

        goToInitialStep();
        return;
      }

      const passwordError = PASSWORD_ERROR_MESSAGES[message];

      if (passwordError) {
        form.setError('password', {
          type: 'request',
          message: passwordError,
        });

        goToSignInStep();
      }
    },
  });

  const { mutate: mutateSignUp, reset: resetSignUp } = useMutation({
    mutationFn: postSignUpUser,
    onSuccess: () => {
      resetSignUp();
      sendEvent({ action: 'signup_success', category: gaEventCategory });
      sendEvent({
        action: 'signup_feedback_appear',
        category: gaEventCategory,
      });

      onClose();
      showDialog(SignUpSuccessDialog);
    },
    onError: ({ message }) => {
      const emailError = EMAIL_ERROR_MESSAGES[message];
      const generalError = GENERAL_ERROR_MESSAGES[message];

      if (emailError || generalError) {
        form.setError('email', {
          type: 'request',
          message: emailError || generalError,
        });

        goToInitialStep();
        return;
      }

      const passwordError = PASSWORD_ERROR_MESSAGES[message];

      if (passwordError) {
        form.setError('password', {
          type: 'request',
          message: passwordError,
        });

        goToSignUpStep();
        signUpStepRef.current.goToPasswordStep();
        return;
      }

      const nameError = NAME_ERROR_MESSAGES[message];

      if (nameError) {
        form.setError('name', {
          type: 'request',
          message: nameError,
        });
        goToSignUpStep();
        signUpStepRef.current.goToNameStep();
      }
    },
  });

  const onSubmit = ({ name, email, password }) => {
    const isSignIn = !name;

    if (isSignIn) {
      sendEvent({ action: 'login_submit', category: gaEventCategory });
      mutateSignIn({ email, password });
      return;
    }

    const utms = getUtmsFromSession();
    let signUpData = { name, email, password };

    if (!isEmpty(utms)) {
      signUpData = {
        ...signUpData,
        ...utms,
      };
    }

    sendEvent({ action: 'signup_submit', category: gaEventCategory });
    mutateSignUp(signUpData);
  };

  const StepComponent = STEPS[currStep];

  return (
    <FormProvider {...form}>
      <form
        className={twJoin('flex flex-col items-center pt-4', className)}
        onSubmit={(event) => {
          event.preventDefault();
        }}
      >
        <StepComponent
          ref={signUpStepRef}
          gaEventCategory={gaEventCategory}
          goToInitialStep={goToInitialStep}
          goToSignInStep={goToSignInStep}
          goToSignUpStep={goToSignUpStep}
          greetingsMessage={greetingsMessage}
          onClose={onClose}
          onSubmit={form.handleSubmit(onSubmit)}
        />
      </form>
    </FormProvider>
  );
};

Form.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  greetingsMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default Form;
